export const insights = [
  {
    slug: "the-premium-positioning-trap",
    author: { name: "", role: "" },
    title: "The premium positioning trap",
    category: "Brand Strategy",
    date: "",
    readTime: "",
    excerpt:
      "Every board says 'premium'. Almost none of them earn the word. The difference is not taste — it is a proof system.",
    sections: [
      {
        h: "Premium is a claim, not a position",
        p: [
          "Ask a leadership team to describe their ideal position and the answer is almost always some version of the same word: premium. It is the most contested seat in every category and the least examined. Companies announce premium-ness in their copy, their decks and their trade-show stands, then wonder why procurement treats them like a line item.",
          "The problem is not ambition. It is that 'premium' describes an outcome, not a mechanism. Premium is what happens when a business makes a specific, verifiable promise to a specific buyer — and then engineers every touchpoint to prove it before a salesperson ever enters the room.",
        ],
      },
      {
        h: "Positioning is a decision about who you lose",
        p: [
          "A real position excludes. If your brand could plausibly serve anyone, it persuades no one. The strongest repositionings we lead begin with an uncomfortable sentence: here is the revenue we are choosing not to chase, and here is the buyer we intend to be unavoidable for.",
          "That decision has consequences the whole system has to absorb. Pricing changes. The proposal template changes. The projects you showcase change. When a contractor decides it sells certainty rather than construction, its case studies stop being galleries of photographs and start being evidence — duration kept, budget held, risk retired.",
        ],
      },
      {
        h: "The proof system does the selling",
        p: [
          "Premium buyers do not pay more for adjectives. They pay more for reduced uncertainty. A proof system is the organised body of evidence — named frameworks, documented outcomes, visible standards, recognisable work — that lets a stranger verify your promise without trusting your salesperson.",
          "Most established businesses already own the raw material for this system. It is scattered across project archives, founder memories and the heads of site managers. The transformation work is editorial: deciding which proof matters, producing it to a standard, and putting it where decisions are actually made.",
        ],
      },
      {
        h: "Where the trap springs",
        p: [
          "The trap closes when a company buys the aesthetics of premium — the film, the rebrand, the photography — without the strategic decision underneath. The result is a business that looks more expensive than it behaves. Buyers feel the gap, and the gap is more damaging than looking ordinary.",
          "Position first. Build the proof system second. Direct the creative third. The order is the strategy.",
        ],
      },
    ],
  },
  {
    slug: "your-sales-system-is-your-brand",
    author: { name: "", role: "" },
    title: "Your sales system is your brand",
    category: "Sales & Commercial Systems",
    date: "",
    readTime: "",
    excerpt:
      "Brand perception is set in the commercial moment — the quote, the proposal, the negotiation. Treat those touchpoints as brand assets or watch them undo your strategy.",
    sections: [
      {
        h: "The brand your buyers actually meet",
        p: [
          "Leadership spends months on brand strategy, then hands the buyer experience to a sales process nobody has designed. The proposal template dates from a lost quarter. Pricing logic lives in one founder's judgement. Follow-up depends on whoever shouts loudest in the pipeline review.",
          "From the buyer's side, this is the brand. The quotation is more persuasive than the campaign — it is the moment your promise gets tested against your behaviour.",
        ],
      },
      {
        h: "Structure is strategy made physical",
        p: [
          "A commercial system encodes what a business believes into repeatable structure: offer tiers that make choice legible, stage definitions that make forecasting honest, playbooks that make quality survivable when the founder is in the room and when they are not.",
          "When we audit a stalled revenue engine, the constraint is rarely effort. It is undisciplined structure — offers assembled per deal, pipelines with stages named after emotions, and pricing negotiated one concession at a time until the margin story no longer adds up.",
        ],
      },
      {
        h: "Pricing is brand strategy in numbers",
        p: [
          "Nothing communicates a position faster than a price. A three-tier architecture tells a buyer where the ceiling is. A single negotiated number tells them everything is negotiable. Discount discipline is not a finance policy — it is a brand statement delivered with an invoice attached.",
          "The fix is architectural, not motivational: package the offer so the premium tier is genuinely different, define what each tier excludes, and give the sales team rules that protect the position without requiring courage in every negotiation.",
        ],
      },
      {
        h: "Build it like a product",
        p: [
          "The commercial system deserves the same rigour as the brand identity: named components, documented logic, version control, a review rhythm. When it is treated as infrastructure, revenue stops being a weather report and becomes an output — which is the entire point of transformation.",
        ],
      },
    ],
  },
  {
    slug: "direct-demand-is-a-strategy-not-a-channel",
    author: { name: "", role: "" },
    title: "Direct demand is a strategy, not a channel",
    category: "Leisure & Hospitality",
    date: "",
    readTime: "",
    excerpt:
      "Owning the guest relationship changes rate integrity, repeat behaviour and margin. Booking-direct buttons are tactics; the strategy is upstream of them.",
    sections: [
      {
        h: "Renting demand is a tax on the brand",
        p: [
          "For resorts, attractions and destination groups, third-party platforms solved distribution — and then quietly became the strategy. Rates compressed to match the algorithm, guests arrived loyal to the platform rather than the property, and the marketing function shrank to bidding against yourself for demand you already earned once.",
          "None of this is an argument against platforms. It is an argument against dependence. When most of your arrivals arrive through someone else's relationship, you do not own a brand; you rent shelf space on one.",
        ],
      },
      {
        h: "Direct is earned upstream of the button",
        p: [
          "A direct-booking strategy is not a 'book direct and save' banner. Guests book direct when the relationship is worth having: recognition before arrival, offers that reward the second stay, on-property experiences that exist nowhere else, and a reason to believe the brand will still be excellent in three years.",
          "That means the work is mostly brand and lifecycle, not promo codes. Positioning gives the group a reason to be chosen as itself. The guest journey converts that choice into data, and the data into a second visit.",
        ],
      },
      {
        h: "Rate integrity is the scoreboard",
        p: [
          "Watch average daily rate and direct share move together and you can read the health of the strategy in two numbers. Discount-led direct programmes train guests to wait; value-led ones — recognition, flexibility, access — raise what the market will pay for belonging.",
          "The measurement layer matters as much as the creative. Channel profitability, guest lifetime value and repeat behaviour on one dashboard turn an opinionated debate into a weekly operating decision.",
        ],
      },
      {
        h: "The compound effect",
        p: [
          "Every direct relationship compounds: cheaper demand, richer data, steadier rates, a brand that outlives any distribution partner. Start with the positioning, build the lifecycle, and let the booking engine be the receipt — not the relationship.",
        ],
      },
    ],
  },
  {
    slug: "ai-wont-fix-an-undocumented-business",
    author: { name: "", role: "" },
    title: "AI won't fix an undocumented business",
    category: "AI & Systemization",
    date: "",
    readTime: "",
    excerpt:
      "The teams getting real leverage from AI did the unglamorous work first: documented process, named owners, measurable steps. Automation amplifies structure — or chaos.",
    sections: [
      {
        h: "Automation is an amplifier",
        p: [
          "The current wave of AI tools promises leverage, and delivers it — for businesses that already run on defined processes. Where workflows are tribal, AI accelerates confusion. It produces faster drafts of unclear thinking and automates handoffs nobody defined in the first place.",
          "This is why pilots stall. The tool works in the demo and fails in the operating reality, because the operating reality is undocumented.",
        ],
      },
      {
        h: "Systemize first, automate second",
        p: [
          "Systemization is the discipline of making the business legible: every recurring workflow written down, every handoff owned, every definition of 'done' explicit. It is slow, unglamorous and the highest-leverage work we do — because every later system, human or machine, runs on it.",
          "Once a workflow is legible, the AI question becomes answerable: where does volume justify automation, where does judgement need to stay human, and where would a knowledge system return hours without touching a decision at all.",
        ],
      },
      {
        h: "The knowledge layer is the asset",
        p: [
          "The durable output of an AI programme is not the tooling — tools change quarterly. It is the knowledge layer: structured processes, captured expertise, retrieval that works at the moment of need. That layer makes every future hire faster and every future tool cheaper to adopt.",
          "Guardrails belong in the same build: review steps, quality thresholds, named accountability for output. Adoption survives on trust, and trust survives on standards.",
        ],
      },
      {
        h: "Measure hours returned, not tools deployed",
        p: [
          "The scoreboard for this work is operational, not technical: hours returned per week, cycle time on the workflows that drain senior people, error rates on the steps that were automated. If a pilot cannot name the hours it will return, it is a toy. Choose the boring pilot with a number attached.",
        ],
      },
    ],
  },
];

export const getInsight = (slug) => insights.find((i) => i.slug === slug);
export const insightCategories = ["All", ...new Set(insights.map((i) => i.category))];
export const nextInsight = (slug) => {
  const i = insights.findIndex((x) => x.slug === slug);
  return insights[(i + 1) % insights.length];
};
